import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, AlertTriangle, Package, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getExecutiveBrief, track } from "../lib/api";

/**
 * Executive Brief — the "money-language" replacement for the
 * "Pulse found 427 things" headline. A CEO sees dollars + actionable counts.
 */
function fmtMoney(n) {
  if (n == null) return "—";
  const v = Math.abs(n);
  if (v >= 1_000_000) return `₪${(n / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `₪${Math.round(n / 1_000)}K`;
  return `₪${Math.round(n)}`;
}

const cards = [
  {
    id: "opportunity",
    label: "הזדמנויות מזוהות",
    icon: TrendingUp,
    color: "text-emerald-300",
    gradient: "from-emerald-500/10 to-emerald-500/0",
    ring: "hover:ring-emerald-400/40",
    valueKey: "opportunity_value",
    subKey: "opportunity_count",
    subTpl: (n) => `${n} מוצרים בצמיחה`,
    fmtValue: fmtMoney,
    onClickRoute: "/insights",
  },
  {
    id: "risk",
    label: "הון לא פעיל",
    icon: AlertTriangle,
    color: "text-red-300",
    gradient: "from-red-500/10 to-red-500/0",
    ring: "hover:ring-red-400/40",
    valueKey: "dead_stock_count",
    subKey: "risk_value",
    subTpl: (n) => `${fmtMoney(n)} כלוא במלאי לא פעיל`,
    fmtValue: (n) => `${n} מק״טים`,
    onClickRoute: "/inventory",
  },
  {
    id: "restock",
    label: "דורש הזמנה",
    icon: Package,
    color: "text-primary",
    gradient: "from-primary/10 to-primary/0",
    ring: "hover:ring-primary/40",
    valueKey: "restock_count",
    subKey: "restock_critical_count",
    subTpl: (n) => `${n} קריטיים (≤3 ימים)`,
    fmtValue: (n) => `${n} מוצרים`,
    onClickRoute: "/smart-restock",
  },
  {
    id: "families",
    label: "תלות במשפחות מוצר",
    icon: Layers,
    color: "text-amber-300",
    gradient: "from-amber-500/10 to-amber-500/0",
    ring: "hover:ring-amber-400/40",
    valueKey: "families_top5_pct_of_profit",
    subKey: "families_total",
    subTpl: (n) => `5 משפחות מובילות מתוך ${n}`,
    fmtValue: (n) => `${n}% מהרווח`,
    onClickRoute: "/families",
    supportedKey: "families_supported",
  },
];

export default function ExecutiveBrief() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getExecutiveBrief()
      .then((r) => setData(r.data))
      .catch(console.error);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
      data-testid="executive-brief"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2.5">
        Executive Brief · במונחים של כסף
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {cards.map((c, i) => {
          const value = data ? c.fmtValue(data[c.valueKey]) : "—";
          const sub = data ? c.subTpl(data[c.subKey]) : "טוען...";
          const supported = !c.supportedKey || (data && data[c.supportedKey]);

          return (
            <motion.button
              key={c.id}
              onClick={() => {
                track("exec_brief_click", "/", { card: c.id });
                navigate(c.onClickRoute);
              }}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 + i * 0.04 }}
              className={`group relative bg-card border border-border rounded-md p-4 text-right hover:border-primary/30 transition-all overflow-hidden ring-1 ring-transparent ${c.ring}`}
              data-testid={`exec-brief-${c.id}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-bl ${c.gradient} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
              <div className="flex items-start justify-between mb-2 relative">
                <c.icon className={`w-4 h-4 ${c.color}`} strokeWidth={1.5} />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 relative">
                {c.label}
              </div>
              <div className={`text-2xl font-bold ${c.color} relative leading-tight`}>
                {supported ? value : "—"}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono relative">
                {supported ? sub : "Family mapping required"}
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}
